import React, { useEffect, useState } from "react";
import { Button } from "@component/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { ChevronDownIcon, MailIcon, UserPlusIcon, XIcon } from "lucide-react";
import { getCompanyInfo, inviteCompanyPermissions } from "@service/user";
import { useSnackbar } from "@hook/use-snackbar";
import { useOuraWorkspace } from "@oura/role";
import { UserPermissionItem } from "@type/company";
import { Popover, PopoverContent, PopoverTrigger } from "@component/popover";
import { RadioGroup, RadioGroupItem } from "@component/radio-group";
import { accessLevels } from "@constant/app";
import { useUserProfileContext } from "@context/user_profile";
import { catchErrorLog } from "@util/sentry";
import { isValidEmail } from "@util/string";

interface AddUserDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onLoad: () => void;
    existingUsers: UserPermissionItem[]
}

export default function AddUserDialog({
    open,
    onOpenChange,
    onLoad,
    existingUsers = []
}: AddUserDialogProps) {
    const snackbar = useSnackbar();
    const [isLoadingUserInfo, currentUserInfo] = useUserProfileContext();
    // Oura workspaces are internal-only: two roles (Owner + Admin), no
    // read-only Member tier. Detection comes from the trusted hook in
    // @oura/role: JWT role only in production (a user-editable
    // ?product=oura URL cannot flip semantics there), URL hint honored
    // outside production for previews. Role controls stay locked until
    // detection resolves so a fast submit can never stamp an unsupported tier.
    const { isOuraWs, roleReady } = useOuraWorkspace();
    const roleOptions = isOuraWs ? accessLevels.filter((l) => l.value === "write") : accessLevels;
    const [email, setEmail] = useState("");
    const [accessLevel, setAccessLevel] = useState<string>("read");
    const [isLoading, setIsLoading] = useState(false);
    const [emailError, setEmailError] = useState<string>("");
    const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);
    const [invitedUsers, setInvitedUsers] = useState<UserPermissionItem[]>([]);
    const [allowedEmailDomains, setAllowedEmailDomains] = useState<string[]>([]);
    const [showDropdown, setShowDropdown] = useState<boolean>(true);
    const [isErrorLimitUser, setIsErrorLimitUser] = useState<boolean>(false);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const dropdownRef = React.useRef<HTMLDivElement>(null);

    // Once Oura detection resolves, normalize the default to the only tier
    // Oura supports (and back to read for non-Oura workspaces).
    useEffect(() => {
        if (!roleReady) return;
        setAccessLevel(isOuraWs ? "write" : "read");
    }, [roleReady, isOuraWs]);

    useEffect(() => {
        return () => {
            setEmail("");
            setAccessLevel("member");
            setEmailError("");
            setInvitedUsers([]);
            setIsLoading(false);
            setAllowedEmailDomains([]);
            setShowDropdown(true);
            setIsErrorLimitUser(false);
        }
    }, [open])

    useEffect(() => {
        if (!isLoadingUserInfo && currentUserInfo) {
            getCompanyInfo(currentUserInfo.company_id).then((resp) => {
                if (resp.data.data.allowed_email_domains) {
                    setAllowedEmailDomains(resp.data.data.allowed_email_domains.domains);
                }
            })
        }
    }, [isLoadingUserInfo, currentUserInfo])

    useEffect(() => {
        if (isValidEmail(email)) {
            setShowDropdown(true);
        } else {
            setShowDropdown(false);
        }
    }, [email])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        setIsLoading(true);

        try {
            const resp = await inviteCompanyPermissions({
                inviteMember: invitedUsers.map((user) => ({ email: user.email, permission: user.permission })),
            });

            if (resp.status !== 200) {
                snackbar.showError(
                    "Failed to add user. Please try again later."
                );
                return;
            }
            snackbar.showSuccess("Invitation has been sent successfully!");

            // Optionally, you can reset the form fields after successful submission
            setEmail("");
            setAccessLevel("member");
            handleClose();
        } catch (error: any) {
            if (error?.response?.status === 400) {
                setIsErrorLimitUser(true);
            }
            setEmailError(error?.response?.data?.message || "Failed to invite member. Please try again later.");
            catchErrorLog(`Error inviting user: ${error}`);
        } finally {
            setIsLoading(false);
            onLoad();
        }
    };

    const handleClose = () => {
        setEmail("");
        setAccessLevel("member");
        setEmailError("");
        setInvitedUsers([]);
        setIsLoading(false);
        onOpenChange(false);
    };

    const handleAddUserManually = () => {
        if (email.trim()) {
            const emailClear = email.trim();

            // Validate email
            if (!emailClear.includes("@")) {
                setEmailError("Please enter a valid email address.");
                snackbar.showError("Please enter a valid email address.");
                return;
            }

            // validation limit max 10 invites at once
            if (invitedUsers.length >= 10) {
                setEmailError("You can invite a maximum of 10 users at once.");
                return;
            }

            // Check against allowed email domains if any
            if (allowedEmailDomains.length > 0) {
                const emailDomain = emailClear.split("@")[1].toLowerCase();
                const isDomainAllowed = allowedEmailDomains.some(
                    (domain) => domain.toLowerCase() === emailDomain
                );
                if (!isDomainAllowed) {
                    setEmailError(
                        `This email domain is not allowed in this workspace. Allowed domains: ${allowedEmailDomains.join(
                            ", "
                        )}`
                    );
                    return;
                }
            }

            // Check if email already exists in workspace
            if (
                existingUsers.some(
                    (existingEmail) => existingEmail.email.toLowerCase() === email.toLowerCase()
                )
            ) {
                setEmailError(
                    "This user has already joined or been invited to the workspace."
                );
                return;
            }

            // Check for duplicates in current invite list
            if (
                invitedUsers.some(
                    (user) => user.email.toLowerCase() === email.toLowerCase()
                )
            ) {
                setEmailError("This email has already been added.");
                return;
            }

            setInvitedUsers([...invitedUsers, {
                user_id: `invited-${Date.now()}`,
                email,
                first_name: "",
                last_name: "",
                permission: accessLevel,
                status: "invited",
                joined_at: "",
                is_external: true,
            }]);
            setEmail("");
            setEmailError("");
        }
    }

    const handleAddUser = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && email.trim()) {
            e.preventDefault();

            const emailClear = email.trim();

            // Validate email
            if (!emailClear.includes("@")) {
                setEmailError("Please enter a valid email address.");
                snackbar.showError("Please enter a valid email address.");
                return;
            }

            // validation limit max 10 invites at once
            if (invitedUsers.length >= 10) {
                setEmailError("You can invite a maximum of 10 users at once.");
                return;
            }

            // Check against allowed email domains if any
            if (allowedEmailDomains.length > 0) {
                const emailDomain = emailClear.split("@")[1].toLowerCase();
                const isDomainAllowed = allowedEmailDomains.some(
                    (domain) => domain.toLowerCase() === emailDomain
                );
                if (!isDomainAllowed) {
                    setEmailError(
                        `This email domain is not allowed in this workspace. Allowed domains: ${allowedEmailDomains.join(
                            ", "
                        )}`
                    );
                    return;
                }
            }

            // Check if email already exists in workspace
            if (
                existingUsers.some(
                    (existingEmail) => existingEmail.email.toLowerCase() === email.toLowerCase()
                )
            ) {
                setEmailError(
                    "This user has already joined or been invited to the workspace."
                );
                return;
            }

            // Check for duplicates in current invite list
            if (
                invitedUsers.some(
                    (user) => user.email.toLowerCase() === email.toLowerCase()
                )
            ) {
                setEmailError("This email has already been added.");
                return;
            }

            setInvitedUsers([...invitedUsers, {
                user_id: `invited-${Date.now()}`,
                email,
                first_name: "",
                last_name: "",
                permission: accessLevel,
                status: "invited",
                joined_at: "",
                is_external: true,
            }]);
            setEmail("");
            setEmailError("");
        }
    };

    const handleRoleChange = (userId: string, newRole: string) => {
        const invitedUsersUpdated = invitedUsers.map((user) => user.user_id === userId ? { ...user, permission: newRole } : user);
        setInvitedUsers(invitedUsersUpdated);
        setOpenPopoverId(null);
    };

    const handleRemoveUser = (userId: string) => {
        setInvitedUsers(invitedUsers.filter((user) => user.user_id !== userId));
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl" closeButtonClassName="right-4 top-4">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <UserPlusIcon className="h-5 w-5 text-blue-600" />
                        Invite to Workspace
                    </DialogTitle>
                    <DialogDescription>
                        Invite members to join your workspace.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="email">Invite by email</Label>
                        {/* <Input
                            id="email"
                            type="email"
                            placeholder="Enter Member's Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full"
                            disabled={isLoading}
                        /> */}
                        <div className="relative">
                            <Input
                                ref={inputRef}
                                id="email"
                                type="email"
                                placeholder="Enter an email address"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    if (emailError) setEmailError("");
                                }}
                                onKeyDown={handleAddUser}
                                className={`w-full pl-10 ${emailError ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                                disabled={isLoading || !roleReady}
                            />

                            <MailIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />

                            {showDropdown && email.trim() && !emailError && (
                                <div
                                    ref={dropdownRef}
                                    className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg"
                                >
                                    <button
                                        type="button"
                                        onClick={handleAddUserManually}
                                        className="w-full px-4 py-2.5 text-left hover:bg-gray-50 transition-colors flex items-center gap-3 group"
                                    >
                                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                            <span className="text-sm font-medium text-blue-600">
                                                {email.trim().charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                {email.trim()}
                                            </p>
                                        </div>
                                        <div className="text-xs text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                                            Add
                                        </div>
                                    </button>
                                </div>
                            )}
                        </div>

                        {emailError ? (
                            <p className="text-xs text-red-600">{emailError}</p>
                        ) : (
                            <p className="text-xs text-gray-500">
                                Add multiple email addresses and set their permissions below.
                            </p>
                        )}
                    </div>

                    {invitedUsers.length > 0 && (
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Workspace members</Label>
                            <div className="rounded-lg border border-gray-200 max-h-64 overflow-y-auto divide-y divide-gray-100">
                                {invitedUsers.map((user, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between p-3"
                                    >
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                                <span className="text-sm font-medium text-blue-600">
                                                    {user.email.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium truncate">
                                                    {user.email}
                                                </p>
                                                {user.status === "sent" && (
                                                    <p className="text-xs text-gray-500">
                                                        Invitation Sent
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <Popover
                                                open={openPopoverId === user.user_id}
                                                onOpenChange={(open) =>
                                                    setOpenPopoverId(open ? user.user_id : null)
                                                }
                                            >
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        className={`h-9 px-3 justify-between font-normal hover:bg-blue-50 ${openPopoverId === user.user_id ? "bg-blue-50" : ""}`}
                                                        disabled={isLoading || !roleReady}
                                                    >
                                                        <span>
                                                            {roleOptions.find((l) => l.value === user.permission)
                                                                ?.label || (isOuraWs ? "Admin" : "Member")}
                                                        </span>
                                                        <ChevronDownIcon className="h-4 w-4 ml-2 text-gray-400" />
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-80 p-0" align="end">
                                                    <div className="p-4">
                                                        <h4 className="font-semibold text-sm mb-3">
                                                            Workspace Role
                                                        </h4>
                                                        <RadioGroup
                                                            value={user.permission}
                                                            onValueChange={(value: string) =>
                                                                handleRoleChange(user.user_id, value)
                                                            }
                                                            className="space-y-3"
                                                        >
                                                            {roleOptions.map((level) => (
                                                                <label
                                                                    key={level.value}
                                                                    className={`flex items-start gap-3 cursor-pointer p-2 rounded-md hover:bg-blue-50 transition-colors ${user.permission === level.value
                                                                        ? "bg-blue-50"
                                                                        : ""
                                                                        }`}
                                                                >
                                                                    <RadioGroupItem
                                                                        value={level.value}
                                                                        className="mt-0.5"
                                                                    />

                                                                    <div className="flex-1">
                                                                        <div className="font-medium text-sm mb-1">
                                                                            {level.label}
                                                                        </div>
                                                                        <div className="text-xs text-muted-foreground leading-relaxed">
                                                                            {level.description}
                                                                        </div>
                                                                    </div>
                                                                </label>
                                                            ))}
                                                        </RadioGroup>
                                                    </div>
                                                </PopoverContent>
                                            </Popover>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-9 w-9 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50"
                                                onClick={() => handleRemoveUser(user.user_id)}
                                                disabled={isLoading}
                                            >
                                                <XIcon className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <DialogFooter className="gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="bg-blue-600 hover:bg-blue-700"
                            disabled={isLoading || !roleReady || invitedUsers.length === 0 || isErrorLimitUser}
                        >
                            {isLoading
                                ? "Sending..."
                                : `Send ${invitedUsers.length > 0 ? `${invitedUsers.length} ` : ""}Invitation${invitedUsers.length !== 1 ? "s" : ""}`}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
